import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, CheckCircle2, Receipt, Download } from 'lucide-react';
import { Customer } from '../lib/customers';
import { useBusiness } from '../contexts/BusinessContext';
import StyledInvoiceDisplay from './StyledInvoiceDisplay';
import SendInvoiceModal from './SendInvoiceModal';
import { supabase } from '../lib/supabaseClient';
import { sendGmailEmail } from '../lib/email';

interface ProductSaleSuccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  saleNumber: string;
  saleData: {
    subtotal: number;
    taxAmount: number;
    total: number;
    discountAmount?: number;
    discountDetails?: {
      source: 'code' | 'custom';
      code?: string;
      description?: string;
      type: 'percentage' | 'fixed';
      value: number;
    };
    items: Array<{
      name: string;
      quantity: number;
      price: number;
    }>;
  };
  customer?: Customer | null;
  paymentMethod: string;
}

export default function ProductSaleSuccessModal({
  isOpen,
  onClose,
  saleNumber,
  saleData,
  customer,
  paymentMethod
}: ProductSaleSuccessModalProps) {
  const [isAnimating, setIsAnimating] = useState(false);
  const [sendingReceipt, setSendingReceipt] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [showSendInvoiceModal, setShowSendInvoiceModal] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const { selectedBusiness } = useBusiness();
  const invoiceRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
    }
  }, [isOpen]);

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!invoiceRef.current) {
      alert('Invoice not ready for download. Please try again.');
      return;
    }

    setDownloadingPdf(true);
    try {
      // Import libraries dynamically
      const [{ default: html2canvas }, { default: jsPDF }] = await Promise.all([
        import('html2canvas'),
        import('jspdf')
      ]);

      // Convert invoice to canvas
      const canvas = await html2canvas(invoiceRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff'
      });

      // Create PDF
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');

      // Calculate dimensions to fit A4
      const imgWidth = 210; // A4 width in mm
      const pageHeight = 295; // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;

      let position = 0;

      // Add first page
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      // Add additional pages if needed
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      // Download PDF
      const filename = `receipt-${saleNumber}.pdf`;
      pdf.save(filename);

    } catch (error) {
      console.error('Failed to generate PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleSendInvoice = () => {
    console.log('🔍 handleSendInvoice called');
    console.log('Customer:', customer);
    console.log('Current showSendInvoiceModal state:', showSendInvoiceModal);
    
    if (!customer) {
      alert('No customer information available. Please add a customer to send invoice.');
      return;
    }
    
    console.log('✅ Opening SendInvoiceModal...');
    setShowSendInvoiceModal(true);
  };

  const handleSendInvoiceMethod = async (method: 'email' | 'sms', allowStripePay: boolean) => {
    if (method === 'email') {
      await handleSendInvoiceEmail(allowStripePay);
    } else {
      // TODO: Implement SMS sending
      alert('SMS sending not yet implemented for product sales');
    }
  };

  const handleSendInvoiceEmail = async (allowStripePay: boolean = true) => {
    if (!customer?.email || !selectedBusiness) {
      alert('Customer email not found');
      return;
    }

    try {
      setIsSendingEmail(true);
      
      // Generate PDF
      const pdfData = await generateInvoicePDF();
      if (!pdfData) return;
      
      // Convert blob to base64
      const reader = new FileReader();
      const base64Promise = new Promise<string>((resolve, reject) => {
        reader.onloadend = () => {
          const base64String = reader.result as string;
          const base64Content = base64String.split(',')[1];
          resolve(base64Content);
        };
        reader.onerror = reject;
      });
      reader.readAsDataURL(pdfData.blob);
      
      const base64Content = await base64Promise;
      
      console.log('PDF attachment prepared:', {
        filename: pdfData.filename,
        contentLength: base64Content.length
      });

      // Generate payment link if Stripe Connect is enabled and allowStripePay is true
      let paymentLinkHtml = '';
      
      if (allowStripePay && selectedBusiness.stripe_connect_account_id && selectedBusiness.online_payments_enabled) {
        console.log('🔍 Creating Stripe payment link for product sale...');
        
        // Create a product sale payment record (similar to booking payments)
        const { data: productSalePayment, error: paymentError } = await supabase
          .from('payments')
          .insert({
            business_id: selectedBusiness.id,
            booking_id: null, // No booking for product sales
            amount: saleData.total,
            method: 'credit_card',
            status: 'pending',
            reference: `Product Sale ${saleNumber}`,
            notes: `Product sale payment for ${saleNumber}`,
            // Add product sale specific metadata
            metadata: {
              type: 'product_sale',
              sale_number: saleNumber,
              items: saleData.items,
              customer_id: customer.id
            }
          })
          .select()
          .single();

        if (paymentError) {
          console.error('Failed to create payment record:', paymentError);
        } else {
          console.log('✅ Product sale payment record created:', productSalePayment.id);
          
          // Generate payment link
          const { data: linkData, error: linkError } = await supabase.functions.invoke('generate-invoice-payment-link', {
            body: {
              paymentId: productSalePayment.id
            }
          });

          if (!linkError && linkData?.paymentUrl) {
            paymentLinkHtml = `
              <div style="margin: 20px 0; padding: 15px; background-color: #f0f9ff; border: 1px solid #0ea5e9; border-radius: 8px;">
                <h3 style="color: #0369a1; margin: 0 0 10px 0;">💳 Pay Online</h3>
                <p style="margin: 0 0 15px 0; color: #374151;">Click the button below to pay securely with your credit card:</p>
                <a href="${linkData.paymentUrl}" 
                   style="display: inline-block; background-color: #0ea5e9; color: white; padding: 12px 24px; text-decoration: none; border-radius: 6px; font-weight: bold;">
                  Pay $${saleData.total.toFixed(2)} Now
                </a>
                <p style="margin: 15px 0 0 0; font-size: 12px; color: #6b7280;">
                  Secure payment powered by Stripe. You will be redirected to a secure payment page.
                </p>
              </div>
            `;
            console.log('✅ Payment link generated:', linkData.paymentUrl);
          }
        }
      }

      // Prepare email content
      const customerName = `${customer.first_name} ${customer.last_name}`;
      const emailSubject = `Receipt for Product Sale ${saleNumber} - ${selectedBusiness.name}`;
      
      const emailBody = `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
          <h2 style="color: #374151;">Receipt for Product Sale</h2>
          
          <p>Dear ${customerName},</p>
          
          <p>Thank you for your purchase! Please find your receipt attached.</p>
          
          <div style="background-color: #f9fafb; padding: 15px; border-radius: 8px; margin: 20px 0;">
            <h3 style="margin: 0 0 10px 0; color: #374151;">Sale Details:</h3>
            <p style="margin: 5px 0;"><strong>Sale Number:</strong> ${saleNumber}</p>
            <p style="margin: 5px 0;"><strong>Date:</strong> ${new Date().toLocaleDateString()}</p>
            <p style="margin: 5px 0;"><strong>Total Amount:</strong> $${saleData.total.toFixed(2)}</p>
            <p style="margin: 5px 0;"><strong>Payment Status:</strong> Paid</p>
          </div>
          
          ${paymentLinkHtml}
          
          <p>If you have any questions about your purchase, please don't hesitate to contact us.</p>
          
          <p>Best regards,<br>
          ${selectedBusiness.name}<br>
          ${selectedBusiness.phone}<br>
          ${selectedBusiness.email}</p>
        </div>
      `;

      // Send email using Gmail integration
      const emailResult = await sendGmailEmail({
        businessId: selectedBusiness.id,
        to: customer.email,
        subject: emailSubject,
        htmlContent: emailBody,
        attachments: [{
          filename: pdfData.filename,
          content: base64Content,
          contentType: 'application/pdf'
        }]
      });

      if (emailResult.success) {
        alert('Invoice sent successfully!');
        console.log('✅ Product sale invoice email sent successfully');
      } else {
        throw new Error(emailResult.error || 'Failed to send email');
      }

    } catch (error) {
      console.error('❌ Failed to send invoice email:', error);
      alert('Failed to send invoice. Please try again.');
    } finally {
      setIsSendingEmail(false);
    }
  };

  const generateInvoicePDF = async (): Promise<{ blob: Blob; filename: string } | null> => {
    try {
      const [{ default: html2canvas }, { default: jsPDF }] = await Promise.all([
        import('html2canvas'),
        import('jspdf')
      ]);
      
      const invoiceElement = invoiceRef.current;
      if (!invoiceElement) {
        alert('Invoice preview not found. Please try again.');
        return null;
      }

      const canvas = await html2canvas(invoiceElement, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff'
      });
      
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgData = canvas.toDataURL('image/png');
      const imgWidth = 210;
      const pageHeight = 295;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;
      
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      
      const filename = `receipt-${saleNumber}.pdf`;
      const blob = pdf.output('blob');
      
      return { blob, filename };
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
      return null;
    }
  };

  if (!isOpen && !isAnimating) return null;

  // Prepare sale data for styled invoice display
  const saleForInvoice = {
    saleNumber,
    saleData: {
      ...saleData,
      // Transform discount data for StyledInvoiceDisplay
      discounts: saleData.discountDetails ? [{
        id: 'discount-1',
        title: saleData.discountDetails.source === 'code' 
          ? `Code: ${saleData.discountDetails.code}` 
          : (saleData.discountDetails.description || 'Custom Discount'),
        type: saleData.discountDetails.type,
        amount: saleData.discountDetails.value
      }] : undefined,
      total_discounts: saleData.discountAmount,
      subtotal_after_discounts: saleData.discountAmount ? saleData.subtotal - saleData.discountAmount : undefined
    },
    paymentMethod,
    saleDate: new Date().toISOString()
  };

  const modalContent = (
    <div 
      className={`fixed inset-0 bg-black transition-opacity duration-300 ${
        isOpen ? 'bg-opacity-50' : 'bg-opacity-0'
      } z-[100040]`}
      onClick={onClose}
      onTransitionEnd={handleTransitionEnd}
    >
      <div className="flex items-center justify-center min-h-screen p-4">
        <div 
          className={`relative w-full max-w-4xl bg-white rounded-2xl shadow-2xl transform transition-all duration-300 ${
            isOpen ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-green-600 to-emerald-600 text-white p-4 rounded-t-2xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 bg-white/20 rounded-xl flex items-center justify-center">
                  <CheckCircle2 className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-bold">Sale Complete!</h2>
                  <p className="text-green-100 text-sm">Payment processed successfully</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-white/80 hover:text-white p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Invoice Display */}
          <div className="max-h-[70vh] overflow-y-auto">
            {selectedBusiness && (
              <StyledInvoiceDisplay
                sale={saleForInvoice}
                business={selectedBusiness}
                customer={customer}
                forwardRef={invoiceRef}
                className="invoice-preview"
              />
            )}
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-gray-200 flex gap-3">
            <button
              onClick={handleDownloadPdf}
              disabled={downloadingPdf}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gray-600 text-white rounded-xl hover:bg-gray-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Download className="h-4 w-4" />
              {downloadingPdf ? 'Generating...' : 'Download PDF'}
            </button>
            <button
              onClick={handleSendInvoice}
              disabled={isSendingEmail || !customer}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Receipt className="h-4 w-4" />
              {isSendingEmail ? 'Sending...' : 'Send Receipt'}
            </button>
            <button
              onClick={onClose}
              className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors font-medium"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // Debug logging
  console.log('🔍 ProductSaleSuccessModal render:', {
    isOpen,
    showSendInvoiceModal,
    customer: customer ? `${customer.first_name} ${customer.last_name}` : 'No customer',
    customerEmail: customer?.email,
    hasStripeConnected: !!(selectedBusiness?.stripe_connect_account_id && selectedBusiness?.online_payments_enabled)
  });

  return (
    <>
      {createPortal(modalContent, document.body)}
      
      {/* Send Invoice Modal - Render in separate portal */}
      {createPortal(
        <SendInvoiceModal
          isOpen={showSendInvoiceModal}
          onClose={() => {
            console.log('🔍 SendInvoiceModal onClose called');
            setShowSendInvoiceModal(false);
          }}
          onSelectMethod={handleSendInvoiceMethod}
          customerEmail={customer?.email}
          customerPhone={customer?.phone}
          hasStripeConnected={!!(selectedBusiness?.stripe_connect_account_id && selectedBusiness?.online_payments_enabled)}
        />,
        document.body
      )}
    </>
  );
}
